// Graphs.js

// @flow
import * as React from 'react';
import * as d3 from 'd3';
import * as _ from 'lodash';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import Slider from 'react-slick';
import Graph from './Graph';

import type { AppStateType, DataType } from '../Types';

import helpers from '../util/helpers';

type Props = {
  appState: AppStateType,
  group: string,
};

type State = {
  horizontalCueVisible: boolean,
  pointerEvents: boolean,
};

const Graphs = observer(class Graphs extends React.Component<Props, State> {

  sliderRef: { current: null | HTMLDivElement }

  constructor(props) {
    super(props);
    this.state = {
      horizontalCueVisible: true,
      pointerEvents: false
    };
    this.sliderRef = React.createRef();

  }

  componentDidUpdate() {
    // if(this.sliderRef.current !== null) {
    //   this.sliderRef.current.slickGoTo(0, true);
    // }

     /* =========================== */

    var d3n = 0;
    var d3n_max = 0;
    // console.log("endall setup");

    d3.selectAll('g')
      .each(function() { // I believe you could do this with .on('start', cb) but I haven't tested it
          d3n++; d3n_max++;
      })
      .transition()
      .on('end', function() { // use to be .each('end', function(){})
          d3n--;
          if (!d3n) { endall(); }
      })

    function endall() {
        // console.log("endall fired" + d3n_max);
        let div1 = document.createElement('span');
        div1.className = "restpack_shutter";
        document.body.appendChild(div1);
    }
    /* =========================== */

  } // end componentDidUpdate()

  getDataThisGroup() : DataType[] {
    return this.props.appState.groups[this.props.group];
  }

  getChemicalData(chemical) : DataType[] {
    let data = this.props.appState.chemicals[this.props.group + "-" + chemical];
    data = data.sort((function (a, b) {
      if (!a.detected && !b.detected) {return 0}
      if (!a.detected && b.detected) {return -1}
      if (a.detected && !b.detected) {return 1}
      if (a.value > b.value) {return 1}
      if (a.value < b.value) {return -1}
      return 0
    }))

    for (let i = 0; i < data.length; i++) {
      if (data[i].detected) {
        data[i].percentile = i/data.length
      } else {
        data[i].percentile = 0
      }
    }

    return data
  }

  getGraphMetaData(data) {
    // data is already sorted
    // in case of multiple results per chemical, return the highest percentile
    let myPercentiles = []
    let numDetected = 0;
    let meDetected = false;
    for (let d = 0; d < data.length; d++) {
      // find percentile
      if (data[d].mine) {
        // if (!sortedData[d].detected) {
        //   myPercentiles.push(0)
        // } else {
        //   myPercentiles.push(d/sortedData.length)
        // }
        myPercentiles.push(data[d].percentile)

        // save if I'm detected
        if (data[d].detected) {meDetected = true}
      }

      // count nondetected
      if (data[d].detected) {numDetected++}
    }
    let percentile = Math.max(...myPercentiles)
    return [meDetected, percentile, numDetected]
  }

  getDetectedData() : DataType[] {
    return _.filter(this.getDataThisGroup(), { detected: true });
  }

  getYLimits() : number[] {
    //console.log(d3.extent(_.map(this.getDetectedData(), 'value')));
    return d3.extent(_.map(this.getDetectedData(), 'value'));
  }

  // Since these handlers are attached to both onClick and onTouchStart on the same element, we need a workaround to keep both from firing
  // when on a computer (see https://github.com/facebook/react/issues/9809)
  onClick(e) : void {
    this.setState({ horizontalCueVisible: false });
  }

  onTouchStart(e) : void {
    this.setState({ horizontalCueVisible: false });
    setTimeout(() => { this.setState({ pointerEvents: true }); }, 200);
  }

  graph_event_log(num,chemical) : void {
    helpers.eventLog("viewing graph - " + num + " - chem:" + chemical);
  }

  render() {
    if(this.props.appState.data.length === 0) {
      return (
        <div className='graphs loading'>
          <FormattedMessage id='loading' defaultMessage='Cargando...' />
        </div>
      );
    }

    const myData = _.filter(this.getDataThisGroup(), { mine: true });
    const medium : string = _.uniq(_.map(myData, 'medium'))[0];

    //method1--sort graphs with max value of any visit
    //const myData_HiToLoMax = _.orderBy(myData, ['value'], ['desc']); // sort the graphs -- we want the highest single values (from each visit) to rank the graph order  XXX ideally this would be 1)average across visits and 2)relative to benchmarks

    //method2--sort graphs with max avg of visits
    const myData_Avgs =_.chain(myData)
                .groupBy("chemical")
                .mapValues((v,i) => { return {
                                chemical: v[0].chemical,
                                chemical_full: v[0].chemical_full,
                                value_numvisits: v.length,
                                value_avg: _.meanBy(v, 'value') }})
                .value();
    const myData_HiToLoAvgs = _.orderBy(myData_Avgs, ['value_avg'], ['desc'])

    // sorted graphs! use either myData_HiToLoMax or myData_HiToLoAvgs
    const chemicals : string[] = _.uniq(_.map(myData_HiToLoAvgs, 'chemical'));
    const chemicals_full : string[] = _.uniq(_.map(myData_HiToLoAvgs, 'chemical_full'));

    const yLimits = this.getYLimits();

    // console.log("myData_HiToLoAvgs", myData_HiToLoAvgs);


    /*
    const horizontalCue = this.state.horizontalCueVisible === true ?
      (
        <div className='horizontal-cue' onClick={this.onClick.bind(this)} onTouchStart={this.onTouchStart.bind(this)}>
          <div><FormattedMessage id='swipe-right' defaultMessage='Swipe right' /></div>
        </div>
       ) : null;
    */
    const horizontalCue = null;

    let graphs = []

    for (let c = 0; c < chemicals.length; c++) {
      let chemical = chemicals[c]
      let data = this.getChemicalData(chemical)
      let anyDetects = false;
      for (let d = 0; d < data.length; d++) {
        if (data[d].detected) {
          anyDetects = true;
          break;
        }
      }
      if (anyDetects) {
        graphs.push((<Graph
            key={chemical + c}
            appState={this.props.appState}
            data={this.getChemicalData(chemical)}
            chemical={chemical}
            chemical_full={chemicals_full[c]}
            medium={medium}
            yLimits={yLimits}
            chemicalGroup={this.props.group}
            pointerEvents={this.state.pointerEvents} />))
      }
    }

    let self = this;
    graphs.sort(function(a,b) {
      let [a_detected, a_percentile, a_numDetected] = self.getGraphMetaData(a.props.data)
      let [b_detected, b_percentile, b_numDetected] = self.getGraphMetaData(b.props.data)
      if (!a_detected && !b_detected) {
        // when neither is detected, sort by which has more detects;
        if (a_numDetected > b_numDetected) {return -1}
        if (a_numDetected < b_numDetected) {return 1}
        return 0
      }
      if (a_percentile > b_percentile) {return -1}
      if (b_percentile < a_percentile) {return 1}
      return 0
    })

    // const graphs : Graph[] = chemicals.filter((chemical) => {
    //   return _.filter(this.getChemicalData(chemical), function(d){
    //     // console.log(d);
    //       // { mine: true }).length > 0;
    //       return d
    //     })
    // }).map((chemical, i) => {
    //   return (<Graph
    //       key={chemical + i}
    //       appState={this.props.appState}
    //       data={this.getChemicalData(chemical)}
    //       chemical={chemical}
    //       chemical_full={chemicals_full[i]}
    //       medium={medium}
    //       yLimits={yLimits}
    //       chemicalGroup={this.props.group}
    //       pointerEvents={this.state.pointerEvents} />)
    // })

    if (helpers.findGetParameter('print'))
    {
      // inline for print #3 -- tile the graphs instead of slick slider
      return (<div className="printgraphswrapper">{graphs}</div>);
    }
    else
    {

      // infinite={false} dots={true} useTransform={false} ref={this.sliderRef}
      const slider_settings = {
          infinite: false,
          dots: true,
          useTransform: false,
          ref: this.sliderRef,
          init:  this.graph_event_log(0, chemicals[0]),
          afterChange: current => this.graph_event_log(current, chemicals[current])
      }

      let render_result = (
        <div>
          {horizontalCue}
          <div >
            {graphs}
          </div>
        </div>
        );

      return render_result;
    }

  } // end-render

}); // end Graphs



export default Graphs;



// WEBPACK FOOTER //
// ./src/components/Graphs.js